import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Bell, AlertTriangle, CalendarClock, CalendarDays } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Badge } from "./ui/badge";

const POLL_MS = 120000;

export default function FollowupBell() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ overdue: 0, today: 0, upcoming_7d: 0 });
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/followup-summary");
      setSummary(data || {});
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadSummary();
    const t = setInterval(loadSummary, POLL_MS);
    return () => clearInterval(t);
  }, [loadSummary]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/leads/my-followups", { params: { scope: "overdue" } });
        setItems((data?.items || []).slice(0, 6));
      } catch { setItems([]); }
    })();
  }, [open]);

  const overdue = summary.overdue || 0;
  const today = summary.today || 0;
  const badgeCount = overdue + today;

  const go = (scope) => { setOpen(false); navigate(`/leads?followup=${scope}`); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative h-9 w-9 rounded-md flex items-center justify-center hover:bg-muted transition-colors"
          data-testid="followup-bell-btn"
          aria-label="Follow-up reminders"
        >
          <Bell className="h-5 w-5 text-muted-foreground" />
          {badgeCount > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center ${overdue > 0 ? "bg-red-600" : "bg-amber-500"}`}
              data-testid="followup-bell-count"
            >
              {badgeCount > 99 ? "99+" : badgeCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="followup-bell-popover">
        <div className="px-4 py-3 border-b border-border">
          <div className="font-heading font-semibold text-sm">Follow-up reminders</div>
          <div className="text-[11px] text-muted-foreground">Your assigned leads needing attention</div>
        </div>
        <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
          <button type="button" onClick={() => go("overdue")} className="py-2.5 hover:bg-muted/50 transition-colors" data-testid="bell-overdue-btn">
            <div className="text-lg font-bold text-red-600 flex items-center justify-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{overdue}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Overdue</div>
          </button>
          <button type="button" onClick={() => go("today")} className="py-2.5 hover:bg-muted/50 transition-colors" data-testid="bell-today-btn">
            <div className="text-lg font-bold text-amber-600 flex items-center justify-center gap-1"><CalendarClock className="h-3.5 w-3.5" />{today}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Today</div>
          </button>
          <button type="button" onClick={() => go("upcoming")} className="py-2.5 hover:bg-muted/50 transition-colors" data-testid="bell-upcoming-btn">
            <div className="text-lg font-bold text-foreground flex items-center justify-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{summary.upcoming_7d || 0}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">7 days</div>
          </button>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">No overdue follow-ups 🎉</div>
          ) : (
            items.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => go("overdue")}
                className="w-full text-left px-4 py-2.5 hover:bg-muted/50 transition-colors border-b border-border/60 last:border-0"
                data-testid={`bell-item-${l.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{l.name || l.mobile}</span>
                  <Badge variant="outline" className="text-[9px] capitalize shrink-0">{l.status}</Badge>
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {l.mobile}{l.next_follow_up_date ? ` · due ${l.next_follow_up_date}` : ""}
                </div>
              </button>
            ))
          )}
        </div>
        {overdue > 6 && (
          <button type="button" onClick={() => go("overdue")} className="w-full py-2 text-xs text-primary hover:bg-muted/50 border-t border-border" data-testid="bell-view-all-btn">
            View all {overdue} overdue →
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
